/**
 * Graphes, mode « Markov » : un graphe probabiliste à 2 ou 3 états, ses
 * probabilités en dixièmes ; πₙ = π₀Pⁿ étape par étape (barres et courbes),
 * la distribution invariante π = πP (état stable) en fractions exactes.
 */

import { useState } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, Segmented, Stepper } from '../../ui/controls';
import { frenchSpaces } from '../../ui/format';
import { FIGURE_BG_CLASS } from '../../ui/svg-parts';
import { atoms, matTex, q, show, toNumber, type Q } from '../matrix-calc/matrix';
import { Live } from '../matrix-calc/matrix-ui';
import { GraphFigure, type GEdge } from './graph-figure';
import { NAMES, converges, distribution, stationary, transition } from './markov';

const START: Record<2 | 3, number[][]> = {
  2: [
    [8, 2],
    [4, 6],
  ],
  3: [
    [6, 3, 1],
    [2, 6, 2],
    [1, 4, 5],
  ],
};

const COLORS = ['var(--color-primary-500)', 'var(--color-accent-500)', 'var(--color-success-500)'];
const tenth = (v: number) => (v === 10 ? '1' : v === 0 ? '0' : `0,${v}`);

/** πₙ en LaTeX, vecteur ligne ; « ≈ » si une valeur est arrondie */
function rowTex(v: readonly Q[]): { tex: string; exact: boolean } {
  const s = v.map(show);
  return {
    tex: `\\begin{pmatrix} ${s.map((x) => x.tex).join(' & ')} \\end{pmatrix}`,
    exact: s.every((x) => x.exact),
  };
}

const SUB = '₀₁₂₃₄₅₆₇₈₉';
const subscript = (k: number) =>
  String(k)
    .split('')
    .map((c) => SUB[Number(c)])
    .join('');

const CW = 320;
const CH = 150;
const NMAX = 20;

export function MarkovMode({ onSend, sendCost }: ToolProps) {
  const [size, setSize] = useState<2 | 3>(2);
  const [t, setT] = useState<number[][]>(START[2]);
  const [start, setStart] = useState(0);
  const [n, setN] = useState(1);

  const P = transition(t);
  const pi0 = t.map((_, i) => q(i === start ? 1 : 0));
  const pin = distribution(P, pi0, n);
  const st = stationary(P);
  const conv = converges(P);
  const names = NAMES.slice(0, size);

  const edges: GEdge[] = [];
  t.forEach((row, i) =>
    row.forEach((v, j) => {
      if (v > 0) edges.push({ from: i, to: j, label: tenth(v) });
    }),
  );

  // une probabilité hors diagonale ; la diagonale prend le reste
  const setOff = (i: number, j: number, v: number) =>
    setT((m) =>
      m.map((row, a) => {
        if (a !== i) return row;
        const next = row.map((x, b) => (b === j ? v : x));
        const off = next.reduce((s, x, b) => (b === a ? s : s + x), 0);
        return next.map((x, b) => (b === a ? 10 - off : x));
      }),
    );

  // courbes : πₖ(état) pour k = 0 … NMAX (flottants)
  const curves = names.map((_, s) => {
    const pts: string[] = [];
    let cur = pi0.map(toNumber);
    const F = P.map((r) => r.map(toNumber));
    for (let k = 0; k <= NMAX; k++) {
      pts.push(
        `${((k / NMAX) * (CW - 40) + 28).toFixed(1)},${(CH - 18 - cur[s]! * (CH - 30)).toFixed(1)}`,
      );
      cur = F[0]!.map((_, j) => cur.reduce((acc, x, i) => acc + x * F[i]![j]!, 0));
    }
    return pts.join(' ');
  });
  const xOf = (k: number) => (k / NMAX) * (CW - 40) + 28;
  const yOf = (p: number) => CH - 18 - p * (CH - 30);
  const pn = rowTex(pin);
  const sumTex = names.map((m) => `\\pi_${m}`).join(' + ');

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Nombre d’états"
        value={String(size) as '2' | '3'}
        options={[
          { value: '2', label: '2 états' },
          { value: '3', label: '3 états' },
        ]}
        onChange={(v) => {
          const s = Number(v) as 2 | 3;
          setSize(s);
          setT(START[s]);
          setStart(0);
        }}
      />

      <GraphFigure
        n={size}
        edges={edges}
        oriented
        label={`Graphe pondéré de la chaîne de Markov à ${size} états ${names.join(', ')}`}
      />

      <div className="grid grid-cols-2 gap-2">
        {t.flatMap((row, i) =>
          row.map((v, j) =>
            i === j ? null : (
              <Stepper
                key={`${i}${j}`}
                label={`${NAMES[i]} → ${NAMES[j]}`}
                labelAbove
                value={v}
                min={0}
                max={v + row[i]!}
                onChange={(x) => setOff(i, j, x)}
                format={tenth}
              />
            ),
          ),
        )}
      </div>
      <p className="text-ink-600 text-sm">
        {frenchSpaces(
          'La probabilité de rester sur place (la boucle) prend le reste : chaque ligne de P a pour somme 1.',
        )}
      </p>

      <div className="space-y-2">
        <MathText className="text-ink-900 block overflow-x-auto">{`$P = ${matTex(P).tex}$`}</MathText>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          ariaLabel="État de départ"
          value={String(start)}
          options={names.map((nm, i) => ({ value: String(i), label: `Départ ${nm}` }))}
          onChange={(v) => setStart(Number(v))}
        />
      </div>
      <div className="max-w-56">
        <Stepper label="étape n" value={n} min={0} max={30} onChange={setN} />
      </div>

      <section
        className="border-ink-100 space-y-2 rounded-2xl border px-3 py-2"
        data-testid="mk-step"
      >
        <Live text={`π${subscript(n)} = (${pin.map((x) => show(x).plain).join(' ; ')})`} />
        <MathText className="text-ink-900 block overflow-x-auto">
          {`$\\pi_{${n}} = \\pi_0 P^{${n}} ${pn.exact ? '=' : '\\approx'} ${pn.tex}$`}
        </MathText>
        <ul className="space-y-1">
          {pin.map((x, s) => {
            const v = toNumber(x);
            const sv = st ? toNumber(st[s]!) : null;
            return (
              <li key={s} className="flex items-center gap-2">
                <span className="text-ink-800 w-5 text-sm font-bold">{NAMES[s]}</span>
                <span className="bg-ink-100 relative h-4 flex-1 overflow-hidden rounded-full">
                  <span
                    className="absolute inset-y-0 left-0 rounded-full"
                    style={{ width: `${(v * 100).toFixed(2)}%`, background: COLORS[s] }}
                  />
                  {sv !== null && conv && (
                    <span
                      aria-hidden
                      className="bg-ink-900 absolute inset-y-0 w-0.5"
                      style={{ left: `${(sv * 100).toFixed(2)}%` }}
                    />
                  )}
                </span>
                <span className="text-ink-900 w-16 text-right text-sm tabular-nums">
                  {show(x).exact ? '' : '≈ '}
                  {show(x).plain}
                </span>
              </li>
            );
          })}
        </ul>
        <p className="text-ink-600 text-sm">
          {frenchSpaces(
            `On part de ${NAMES[start]} (π₀ a un 1 en ${NAMES[start]}) ; à chaque étape on multiplie par P : πₙ₊₁ = πₙP. Le petit trait vertical marque la distribution invariante.`,
          )}
        </p>
      </section>

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          viewBox={`0 0 ${CW} ${CH}`}
          className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
          role="img"
          aria-label={`Évolution de πₙ pour n de 0 à ${NMAX}`}
        >
          <line x1={28} y1={yOf(0)} x2={CW - 12} y2={yOf(0)} stroke="var(--color-ink-300)" />
          <line x1={28} y1={yOf(1)} x2={CW - 12} y2={yOf(1)} stroke="var(--color-ink-100)" />
          <text x={12} y={yOf(1) + 4} fontSize={12} fill="var(--color-ink-500)">
            1
          </text>
          <text x={12} y={yOf(0) + 4} fontSize={12} fill="var(--color-ink-500)">
            0
          </text>
          <text x={CW - 16} y={CH - 3} fontSize={12} textAnchor="end" fill="var(--color-ink-500)">
            n = {NMAX}
          </text>
          {n <= NMAX && (
            <line
              x1={xOf(n)}
              y1={8}
              x2={xOf(n)}
              y2={yOf(0)}
              stroke="var(--color-ink-400)"
              strokeDasharray="3 3"
            />
          )}
          {st &&
            conv &&
            st.map((x, s) => (
              <line
                key={`s${s}`}
                x1={28}
                x2={CW - 12}
                y1={yOf(toNumber(x))}
                y2={yOf(toNumber(x))}
                stroke={COLORS[s]}
                strokeDasharray="5 4"
                strokeWidth={1.25}
              />
            ))}
          {curves.map((pts, s) => (
            <polyline key={s} points={pts} fill="none" stroke={COLORS[s]} strokeWidth={2.5} />
          ))}
        </svg>
      </div>

      <section
        className="border-ink-100 space-y-2 rounded-2xl border px-3 py-2"
        data-testid="mk-stable"
      >
        <p className="text-ink-600 text-sm font-semibold">Distribution invariante (état stable)</p>
        <MathText className="text-ink-900 block overflow-x-auto">
          {`$\\pi = \\pi P \\ \\text{et}\\ ${sumTex} = 1 \\iff$`}
        </MathText>
        <MathText className="text-ink-900 block overflow-x-auto">
          {`$\\begin{cases} ${names
            .map(
              (nm, j) =>
                `\\pi_${nm} = ${
                  names
                    .map((m, i) => (t[i]![j]! === 0 ? '' : `${show(P[i]![j]!).tex}\\,\\pi_${m}`))
                    .filter(Boolean)
                    .join(' + ') || '0'
                }`,
            )
            .join(' \\\\ ')} \\\\ ${sumTex} = 1 \\end{cases}$`}
        </MathText>
        {st ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <MathText className="text-ink-900 min-w-0 flex-1 overflow-x-auto">
                {`$\\pi = ${rowTex(st).tex}$`}
              </MathText>
              {onSend && atoms(st[0]!).length > 0 && (
                <SendToSheetButton
                  label={show(st[0]!).plain}
                  cost={sendCost}
                  onClick={() => onSend({ atoms: atoms(st[0]!) })}
                />
              )}
            </div>
            <Note tone={conv ? 'success' : 'neutral'}>
              {frenchSpaces(
                conv
                  ? `Quel que soit le départ, πₙ se rapproche de π : à long terme, on est en A avec une probabilité ${show(st[0]!).plain}.`
                  : 'Une distribution invariante existe, mais πₙ ne s’en approche pas : la chaîne tourne en rond (elle est périodique), πₙ oscille.',
              )}
            </Note>
          </>
        ) : (
          <Note>
            {frenchSpaces(
              'Plusieurs distributions invariantes : depuis certains états, on ne peut jamais atteindre les autres. Le résultat à long terme dépend du départ.',
            )}
          </Note>
        )}
      </section>
    </div>
  );
}
